/**
 * Our own personas, drawn in the shared art system (docs/ART-BIBLE.md §6, style from live config):
 *  - Scribble: a sitting fox kit with a blue paint-dipped tail tip and a yellow scarf (mascot).
 *  - Teacher Juno: a young illustrator, bust view, bun with a pencil, round glasses.
 *  - Profile avatars (fox, cat, bear, bunny, mouse, dog, frog, kid) as Storybook portraits.
 * The custom character builder (`CharacterView`) keeps its own parametric renderer.
 */
import { memo } from 'react';
import { avatarSpec, renderCharacter, type CharacterSpec } from '@sketchling/core';
import { Figure } from '../art/Figure';
import { useArtStyle } from '../art/useArtStyle';
import type { StyleId } from '../art/tokens';
import { JUNO_POSES, SCRIBBLE_POSES, junoPose, scribblePose, type JunoFace, type ScribbleMood } from '../art/subjects/poses';
import { AVATAR_DISC, AVATAR_IDS, AVATAR_PALETTE, avatarFigure, type AvatarId } from '../art/subjects/avatars';

export { JUNO_POSES, SCRIBBLE_POSES, AVATAR_IDS };
export type { JunoFace, ScribbleMood, AvatarId };

export const Scribble = memo(function Scribble({ mood = 'happy', size = 120, className = '', styleId }: { mood?: ScribbleMood; size?: number; className?: string; styleId?: StyleId }) {
  const live = useArtStyle();
  const st = styleId ?? live;
  return <Figure def={scribblePose(st, mood)} styleId={st} className={`scribble mood-${mood} ${className}`} title="Scribble the fox" style={{ width: size, height: size }} />;
});

export const TeacherJuno = memo(function TeacherJuno({ face = 'smile', size = 120, className = '', styleId }: { face?: JunoFace; size?: number; className?: string; styleId?: StyleId }) {
  const live = useArtStyle();
  const st = styleId ?? live;
  return <Figure def={junoPose(st, face)} styleId={st} className={`juno face-${face} ${className}`} title="Teacher Juno" style={{ width: size, height: size }} noGround />;
});
export const Juno = TeacherJuno;

const isAvatarId = (id: string): id is AvatarId => (AVATAR_IDS as readonly string[]).includes(id);

/** A preset profile avatar as a Storybook portrait on a soft disc. */
export const AvatarArt = memo(function AvatarArt({ id, size = 48, className = '', styleId }: { id: string; size?: number; className?: string; styleId?: StyleId }) {
  const live = useArtStyle();
  const st = styleId ?? live;
  const a: AvatarId = isAvatarId(id) ? id : 'fox';
  const pal = AVATAR_PALETTE[st];
  return (
    <span className={`avatar avatar-art av-${a} ${className}`} style={{ width: size, height: size, background: pal[AVATAR_DISC[a]], borderRadius: '50%', overflow: 'hidden', display: 'inline-block', flex: 'none' }} role="img" aria-label={a}>
      <Figure def={avatarFigure(a)} styleId={st} palette={pal} style={{ width: size, height: size, transform: 'translateY(6%) scale(1.04)' }} />
    </span>
  );
});

export function CharacterView({ spec, size = 120, className = '' }: { spec: CharacterSpec; size?: number; className?: string }) {
  return <svg className={className} width={size} height={size} viewBox="0 0 200 200" aria-hidden dangerouslySetInnerHTML={{ __html: renderCharacter(spec) }} />;
}

/** Profile avatar (preset id → Storybook portrait, or the custom character). */
export function Avatar({ avatar, character, size = 48 }: { avatar: string; character?: CharacterSpec; size?: number }) {
  if (avatar === 'custom' && character) return <CharacterView spec={character} size={size} className="avatar" />;
  return <AvatarArt id={avatar} size={size} />;
}

/** Preset spec kept for the character builder's starting point. */
export const presetSpec = (id: string): CharacterSpec => avatarSpec(id);
